import { useEffect, useState, useRef } from "react";
import { createRoot } from 'react-dom/client';

import useEvent from "hooks/useEvent";

import { mergeClassNames } from "utils/common";

const AnimatedDiv = ({
	children = null,
	className = "",
	startTransition = false,
	initialRect = {},
	targetSelector,
	onAnimationEnd: onAnimationEndProp
}) => {
	const [animationQueue, setAnimationQueue] = useState([]);
	const [isAnimating, setIsAnimating] = useState(false);
	const divRef = useRef(null);

	const onAnimationEnd = useEvent(onAnimationEndProp);

	useEffect(() => {
		if (initialRect && targetSelector) {
			setAnimationQueue((prevQueue) => [...prevQueue, { initialRect, targetSelector, onAnimationEnd }]);
		}
	}, [initialRect, targetSelector, onAnimationEnd]);

	useEffect(() => {
		if (!isAnimating && animationQueue.length > 0 && startTransition) {
			setIsAnimating(true);
			const nextAnimation = animationQueue[0];
			animate(nextAnimation);

			setAnimationQueue((prevQueue) => prevQueue.slice(1)); // Dequeue next animation
		}
	}, [animationQueue, isAnimating, startTransition]);

	const animate = ({ initialRect, targetSelector, onAnimationEnd }) => {
		if (!document || !document.body) return;

		const { height, width, top, left } = initialRect;

		const targetElement = document.querySelector(targetSelector);
		if (!targetElement) return;

		const { top: targetTop, left: targetLeft, width: targetWidth, height: targetHeight } = targetElement.getBoundingClientRect();

		const divCenterX = left + width / 2;
		const divCenterY = top + height / 2;
		const targetCenterX = targetLeft + targetWidth / 2;
		const targetCenterY = targetTop + targetHeight / 2;

		const newDiv = document.createElement("div");
		divRef.current = newDiv;
		newDiv.style.position = "absolute";
		newDiv.style.height = `${height}px`;
		newDiv.style.width = `${width}px`;
		newDiv.style.top = `${top}px`;
		newDiv.style.left = `${left}px`;
		newDiv.className = mergeClassNames("vs--animatable-container", className);

		if (children) {
			createRoot(divRef.current).render(children);
		}

		document.body.appendChild(newDiv);

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const translateX = targetCenterX - divCenterX;
				const translateY = targetCenterY - divCenterY;

				newDiv.style.transform = `translate(${translateX}px, ${translateY}px) scale(0.1)`;
				newDiv.classList.add("vs--animatable-container-start-animation");
			});
		});

		const handleTransitionEnd = () => {
			newDiv.remove();
			if (onAnimationEnd) onAnimationEnd();
			setIsAnimating(false);
		};

		newDiv.addEventListener("transitionend", handleTransitionEnd);

		return () => {
			if (newDiv) {
				newDiv.removeEventListener("transitionend", handleTransitionEnd);
				newDiv.remove();
			}
		};
	};

	return children;
};

export default AnimatedDiv;
